/*
 * CategoryCard
 * ------------
 * Standard "section card" used across every step of the fact find. Renders an
 * icon badge, title (with optional count), short description and the section
 * body. Keeps each conceptual section as its own white card on the page so
 * customers can mentally chunk through them rather than seeing one long form.
 */
import React from 'react';

class CategoryCard extends React.Component {
  render() {
    const { icon, title, description, count, children, className, headerExtra } = this.props;
    return (
      <section
        className={
          'rounded-2xl bg-white shadow-sm border border-[#E5E7EB] p-5 sm:p-6 ' +
          (className || '')
        }
      >
        <header className="flex items-start gap-3 mb-5">
          <span
            className="inline-flex items-center justify-center h-10 w-10 rounded-xl flex-shrink-0"
            style={{ backgroundColor: '#EFEAFE', color: '#5B3FE0' }}
          >
            {icon}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base sm:text-lg font-bold text-[#1F2937]">{title}</h3>
              {typeof count === 'number' ? (
                <span
                  className="inline-flex items-center justify-center min-w-[22px] h-5 px-2 rounded-full text-[10px] font-bold"
                  style={{ backgroundColor: '#F5F6FA', color: '#6B7280' }}
                >
                  {count}
                </span>
              ) : null}
              {headerExtra ? <span className="ml-auto">{headerExtra}</span> : null}
            </div>
            {description ? (
              <p className="mt-0.5 text-xs sm:text-sm text-[#6B7280] leading-snug">
                {description}
              </p>
            ) : null}
          </div>
        </header>
        <div className="space-y-4">{children}</div>
      </section>
    );
  }
}

// Inline icon set keyed by purpose. Add new icons here when needed - other
// step files import these so we don't end up with N copies.
export const ICONS = {
  identity: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 10a4 4 0 100-8 4 4 0 000 8zm-7 8a7 7 0 1114 0H3z"
        clipRule="evenodd"
      />
    </svg>
  ),
  contact: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm2.5.5L10 10l5.5-4.5H4.5z" />
    </svg>
  ),
  calendar: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 8h12v8H4V8z"
        clipRule="evenodd"
      />
    </svg>
  ),
  household: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10 2 2 9h2v8h4v-5h4v5h4V9h2L10 2z" />
    </svg>
  ),
  dependents: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M7 8a3 3 0 100-6 3 3 0 000 6zm6 1a2.5 2.5 0 100-5 2.5 2.5 0 000 5zm-6 1c-2.7 0-7 1.3-7 4v2h10v-1c0-1 .4-2.4 1.4-3.4C10.6 11.2 8.6 10 7 10zm6 1c-.4 0-.9 0-1.4.1.9 1 1.4 2.2 1.4 3.4V16h6v-1.5c0-2.2-4-3.5-6-3.5z" />
    </svg>
  ),
  pin: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 2a6 6 0 00-6 6c0 4.5 6 10 6 10s6-5.5 6-10a6 6 0 00-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  history: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 4a1 1 0 10-2 0v4a1 1 0 00.3.7l3 3a1 1 0 001.4-1.4L11 9.6V6z"
        clipRule="evenodd"
      />
    </svg>
  ),
  briefcase: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M7 3a2 2 0 00-2 2v1H4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-1V5a2 2 0 00-2-2H7zm0 3h6V5H7v1z"
        clipRule="evenodd"
      />
    </svg>
  ),
  money: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm6 3a3 3 0 100 6 3 3 0 000-6zm-6 1a1 1 0 100 2 1 1 0 000-2zm12 0a1 1 0 100 2 1 1 0 000-2z"
        clipRule="evenodd"
      />
    </svg>
  ),
  cart: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M3 3a1 1 0 000 2h1l1.7 7.4A2 2 0 007.7 14h6.6a2 2 0 002-1.6L18 6H6.2L5.8 4.7A2 2 0 003.8 3H3zm4 13a2 2 0 100 4 2 2 0 000-4zm9 0a2 2 0 100 4 2 2 0 000-4z" />
    </svg>
  ),
  shield: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 1.5 3.5 4v5.5C3.5 13.5 6.4 17.2 10 18.5c3.6-1.3 6.5-5 6.5-9V4L10 1.5z"
        clipRule="evenodd"
      />
    </svg>
  ),
  book: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M3 3.5A1.5 1.5 0 014.5 2H9v14H4.5A1.5 1.5 0 013 14.5v-11zM11 2h4.5A1.5 1.5 0 0117 3.5v11a1.5 1.5 0 01-1.5 1.5H11V2z" />
    </svg>
  ),
  wrench: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M14.7 3.3a4 4 0 00-5.4 5l-7 7a1 1 0 000 1.4l1 1a1 1 0 001.4 0l7-7a4 4 0 005-5.4l-1.6 1.6a1.5 1.5 0 11-2.1-2.1l1.7-1.5z" />
    </svg>
  ),
  creditCard: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm0 3v2h14V8H3zm2 4h4v1H5v-1z"
        clipRule="evenodd"
      />
    </svg>
  ),
  bank: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10 1 1 5v2h18V5l-9-4zM3 8v7H1v2h18v-2h-2V8h-2v7h-2V8h-2v7H8V8H6v7H4V8H3z" />
    </svg>
  ),
  realEstate: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10 2 2 9h2v8h4v-5h4v5h4V9h2L10 2z" />
    </svg>
  ),
  home: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M3 9a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V9zm2 1v4h10v-4H5zm2-5a3 3 0 016 0v2H7V5z" />
    </svg>
  ),
  savings: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2H3V4zm0 4h14v8a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm3 3a1 1 0 100 2h2a1 1 0 100-2H6z" />
    </svg>
  ),
  shares: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm2-3.586a1 1 0 011 0L8 14.5l3-4 3 2L17 8.5V4a1 1 0 10-2 0v.586l-3.293 3.293L8.5 6 5 10.5l-1 1.5v1.414z" />
    </svg>
  ),
  superFund: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 1.5 3.5 4v5.5C3.5 13.5 6.4 17.2 10 18.5c3.6-1.3 6.5-5 6.5-9V4L10 1.5zm-1 11-2.5-2.5L8 8.5l1 1 3-3 1.5 1.4L9 12.5z"
        clipRule="evenodd"
      />
    </svg>
  ),
  vehicles: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M3 11l1.5-4A2 2 0 016.41 5.5h7.18A2 2 0 0115.5 7l1.5 4v4a1 1 0 01-1 1h-1a1 1 0 01-1-1v-1H6v1a1 1 0 01-1 1H4a1 1 0 01-1-1v-4zm2.5 1.5a1 1 0 100-2 1 1 0 000 2zm9 0a1 1 0 100-2 1 1 0 000 2zM6 7.5l-1 3h10l-1-3H6z" />
    </svg>
  ),
  other: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M2 5a2 2 0 012-2h5l2 2h5a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm9 4a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1z"
        clipRule="evenodd"
      />
    </svg>
  ),
  lightbulb: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 2a6 6 0 00-3.6 10.8V14a2 2 0 002 2h3.2a2 2 0 002-2v-1.2A6 6 0 0010 2zm-2 15a1 1 0 011-1h2a1 1 0 010 2H9a1 1 0 01-1-1z"
        clipRule="evenodd"
      />
    </svg>
  ),
  target: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-3a5 5 0 100-10 5 5 0 000 10zm0-3a2 2 0 100-4 2 2 0 000 4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  trending: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M12 6a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 11-2 0V8.4l-4.3 4.3a1 1 0 01-1.4 0L8 10.4l-4.3 4.3a1 1 0 01-1.4-1.4l5-5a1 1 0 011.4 0L11 10.6 15.6 6H13a1 1 0 01-1-1z" />
    </svg>
  ),
  alert: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M8.3 2.7a2 2 0 013.4 0l6.5 11A2 2 0 0116.5 17h-13a2 2 0 01-1.7-3.3l6.5-11zM10 7a1 1 0 011 1v3a1 1 0 11-2 0V8a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z"
        clipRule="evenodd"
      />
    </svg>
  ),
  refresh: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M4 2a1 1 0 011 1v2.1A8 8 0 0118 10a1 1 0 11-2 0 6 6 0 00-10.5-3.9V6a1 1 0 11-2 0V3a1 1 0 011-1zm12 16a1 1 0 01-1-1v-2.1A8 8 0 012 10a1 1 0 112 0 6 6 0 0010.5 3.9V14a1 1 0 112 0v3a1 1 0 01-1 1z"
        clipRule="evenodd"
      />
    </svg>
  ),
  percent: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M4.3 15.7a1 1 0 010-1.4l10-10a1 1 0 111.4 1.4l-10 10a1 1 0 01-1.4 0zM6 8a2 2 0 100-4 2 2 0 000 4zm8 8a2 2 0 100-4 2 2 0 000 4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  star: (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M10 1.5l2.6 5.3 5.9.8-4.3 4.1 1 5.8L10 14.8l-5.3 2.7 1-5.8L1.5 7.6l5.9-.8L10 1.5z" />
    </svg>
  ),
};

export default CategoryCard;
